/**
 * Decodes the payload (second segment) of a JWT for UI/display purposes only.
 *
 * This is deliberately base64URL-aware. JWTs are base64URL-encoded (RFC 7515),
 * which uses '-' and '_' in place of standard base64's '+' and '/'. The browser's
 * atob() only understands standard base64 and throws InvalidCharacterError when it
 * meets a '-' or '_', so a naive `atob(token.split('.')[1])` silently fails for any
 * token whose payload happens to contain those characters — which occurs whenever a
 * claim carries a non-ASCII byte (e.g. a curly apostrophe in a surname). The
 * percent-encoding step additionally restores multi-byte UTF-8 characters so values
 * like "O’Brien" decode correctly rather than as mojibake.
 *
 * NOTE: This is NOT a security check — the token must still be validated
 * server-side. Returns null if the token is absent or malformed.
 */
export function decodeJwtPayload(token: string | null | undefined): any | null {
  if (!token) {
    return null;
  }
  const segment = token.split('.')[1];
  if (!segment) {
    return null;
  }
  try {
    // base64URL -> base64
    let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    // Restore '=' padding to a length that is a multiple of 4
    const remainder = base64.length % 4;
    if (remainder) {
      base64 += '='.repeat(4 - remainder);
    }
    const binary = atob(base64);
    // Rebuild the original UTF-8 string from the decoded byte sequence
    const json = decodeURIComponent(
      binary
        .split('')
        .map(char => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}
